import React, { useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import DataTableA from '../../../../components/widgets/DataTableA';
import SearchFilterA from '../../../../components/widgets/SearchFilterA';

/**
 * 프로그램 선택 팝업 컴포넌트
 * 공통 팝업 시스템에서 사용하는 프로그램 선택 팝업
 * DataTableA 위젯 컴포넌트 방식으로 구성
 */
const ProgramSelectPopup = ({ config, onClose }) => {
    const { t } = useTranslation();

    // 선택 버튼 클릭 핸들러
    const handleSelectClick = useCallback((programUid, event) => {
        event.stopPropagation(); // 행 클릭 이벤트 전파 방지
        if (programUid) {
            onClose(true, { programUid });
        }
    }, [onClose]);

    // 행 클릭 핸들러 (버튼이 아닌 영역 클릭 시에는 처리하지 않음)
    const handleRowClick = useCallback((row) => {
        // 버튼 클릭은 handleSelectClick에서 처리하므로 여기서는 처리하지 않음
    }, []);

    // SearchFilterA 컴포넌트 설정
    const searchFilterConfig = useMemo(() => ({
        id: 'program-select-searchFilterA',
        targetTableId: 'program-select-table',
        selectOptions: [
            { value: 'progrmNm', label: t('popup.programSelect.searchProgramName') },
            { value: 'url', label: t('popup.programSelect.searchUrl') }
        ]
    }), [t]);

    // DataTableA 컴포넌트 설정 (ProgramManager.jsx 참고)
    const dataTableConfig = useMemo(() => ({
        id: 'program-select-table',
        apiUrl: '/api/system/program/page',
        params: {},
        showTotalCount: true,
        //hideNumberColumn: true, // 번호 컬럼 숨김
        columns: [
            { key: 'progrmNm', label: t('popup.programSelect.colProgramName'), align: 'left', width: '25%' },
            { key: 'uid', label: t('popup.programSelect.colUid'), align: 'left', width: '10%' },
            { key: 'url', label: t('popup.programSelect.colUrl'), align: 'left', width: '30%' },
            { key: 'pageType', label: t('popup.programSelect.colPageType'), align: 'center', width: '15%' },
            {
                key: 'action',
                label: t('popup.programSelect.colAction'),
                align: 'center',
                width: '80px',
                renderType: 'button',
                buttonText: t('popup.programSelect.selectButton'),
                buttonClass: 'btn btn-sm btn-primary',
                style: {
                    minHeight: 'auto !important'
                }
            }
        ],
        onRowClick: handleRowClick
    }), [handleRowClick, t]);

    // 테이블 렌더링 후 버튼 클릭 이벤트 바인딩
    useEffect(() => {
        const bindButtonEvents = () => {
            const tableElement = document.getElementById('program-select-table');
            if (!tableElement) return;

            const tbody = tableElement.querySelector('tbody');
            if (!tbody) return;

            // 모든 선택 버튼에 클릭 이벤트 바인딩
            const selectButtons = tbody.querySelectorAll('.btn-select-program');
            selectButtons.forEach((button) => {
                const programUid = button.getAttribute('data-program-uid');
                if (programUid) {
                    // 기존 이벤트 리스너 제거 후 새로 추가 (중복 방지)
                    button.removeEventListener('click', button._selectHandler);
                    button._selectHandler = (e) => handleSelectClick(programUid, e);
                    button.addEventListener('click', button._selectHandler);
                }
            });
        };

        // 초기 바인딩 (약간의 지연을 두어 DOM이 준비된 후 실행)
        const timer = setTimeout(bindButtonEvents, 200);

        // MutationObserver로 테이블 업데이트 감지
        const tableElement = document.getElementById('program-select-table');
        if (tableElement) {
            const observer = new MutationObserver(() => {
                bindButtonEvents();
            });

            const tbody = tableElement.querySelector('tbody');
            if (tbody) {
                observer.observe(tbody, { childList: true, subtree: true });
            }

            return () => {
                clearTimeout(timer);
                observer.disconnect();
                // 이벤트 리스너 정리
                const buttons = tableElement.querySelectorAll('.btn-select-program');
                buttons.forEach((button) => {
                    if (button._selectHandler) {
                        button.removeEventListener('click', button._selectHandler);
                    }
                });
            };
        }

        return () => {
            clearTimeout(timer);
        };
    }, [handleSelectClick]);

    return (
        <PopupWrapper
            config={config}
            onClose={onClose}
            displayType={config?.displayType || 'modal'}
            className="program-select-popup"
        >
            <style>{`
                #program-select-table .btn-select-program {
                    min-height: auto !important;
                    padding: 2px 8px !important;
                }
            `}</style>
            <div className="popup-content">
                {/* SearchFilterA 컴포넌트 */}
                <div className="mb-3">
                    <SearchFilterA component={searchFilterConfig} />
                </div>

                {/* DataTableA 컴포넌트 */}
                <DataTableA component={dataTableConfig} />
            </div>
        </PopupWrapper>
    );
};

export default ProgramSelectPopup;

